import {
  QUICK_VIEWS,
  EMPTY_FILTERS,
  filterUnits,
  type CuadreAccount,
  type CuadreUnit,
  type RawAccount,
  type Report,
} from "./cuadre";
import { PLATFORMS, PLATFORM_META, type SgPlatform } from "./platforms";
import { formatOrigins, fetchOrigins } from "./origins";
import { buildPlatformRows, conditionKeys, type PlatformTab } from "./plataformas";
import type { RetransmissionsByCompany } from "./retransmissions";
import { BILLING_LABEL, buildHomologado, barridoMonth, homologadoFileName } from "./homologado";
import { buildZip } from "./zip";
import { downloadBytes, downloadXlsx, type XlsxCell, type XlsxSheet } from "./xlsx";
import { sgSupabase } from "../../lib/sgSupabaseClient";
import { fetchLastPositions } from "./socket";
import { notifyError } from "./notify";
import { BARRIDO_SELECT, fetchAllLineas } from "./equipos";
import { fetchAllSgUsers, fetchImeisByUser } from "./usuarios";
import { BARRIDO_HEADERS, LINEAS_HEADERS, barridoRow, fileDate, lineaRow, userRow } from "./exportRows";

// Informes de SG. Los que salen del cuadre (unidades, descuadres, plataformas,
// facturación, barridos homologados) son puros sobre lo que ya cargó la
// pantalla; barrido, líneas y usuarios leen Supabase como antes en sus áreas.
// Las etiquetas van en español fijo: TAG_TONES de xlsx.ts las pinta por valor.

export const REPORT_LABEL: Record<Report, string> = {
  ok: "Reporta (24 h)",
  week: "1 a 7 días",
  old: "Más de 7 días",
  none: "Sin dato",
};

const yes = (b: boolean) => (b ? "Sí" : "");
const date = (ms: number | null) => (ms == null ? null : new Date(ms));

export function unitsSheet(units: CuadreUnit[], name = "Unidades"): XlsxSheet {
  return {
    name,
    headers: [
      "imei", "alias", "placas", "cuenta", "razon social", "facturacion", "etiqueta de equipo", "etiqueta de cuenta",
      "plataformas", "ultimo reporte", "reporte", "origen distinto", "linea telefonica", "simcard", "modelo", "revisar",
    ],
    rows: units.map((u) => [
      u.imei, u.name, u.plates, u.accountName, u.accountCompany, BILLING_LABEL[u.billing], u.condition ?? "", u.accountCondition ?? "",
      formatOrigins(u.platforms), date(u.lastReport), REPORT_LABEL[u.report], yes(u.accountMismatch), u.phone, u.simcard, u.deviceType,
      [u.dupImei && "IMEI repetido", u.tagMismatch && "Activo con etiqueta de baja"].filter(Boolean).join(" · "),
    ]),
  };
}

export function accountsSheet(accounts: CuadreAccount[], name = "Cuentas"): XlsxSheet {
  return {
    name,
    headers: ["cuenta", "facturacion", "etiqueta", "unidades sg", "dumax v5", "wialon", "mapon", "sin plataforma", "reportan", "sin reportar", "origen en sg", "origen real", "origen distinto"],
    rows: accounts.map((a) => [
      a.name, a.billable ? "Facturable" : "No facturable", a.condition ?? "", a.units, a.byPlatform.v5, a.byPlatform.wialon, a.byPlatform.mapon,
      a.byPlatform.none, a.reporting, a.silent, formatOrigins(a.sgOrigin), formatOrigins(a.realOrigin), yes(a.mismatch),
    ]),
  };
}

// Una hoja por pregunta del cuadre: lo que hay que corregir, ya separado.
export function descuadresSheets(units: CuadreUnit[], accounts: CuadreAccount[]): XlsxSheet[] {
  const by = (v: keyof typeof QUICK_VIEWS) => filterUnits(units, { ...EMPTY_FILTERS, ...QUICK_VIEWS[v] });
  return [
    unitsSheet(by("billableSilent"), "Facturables sin reportar"),
    unitsSheet(by("notBillableReporting"), "No facturables que reportan"),
    unitsSheet(by("noPlatform"), "Facturables sin plataforma"),
    unitsSheet(by("multi"), "En varias plataformas"),
    unitsSheet(units.filter((u) => u.dupImei), "IMEI repetido"),
    unitsSheet(units.filter((u) => u.tagMismatch), "Activos con etiqueta de baja"),
    accountsSheet(accounts.filter((a) => a.mismatch), "Cuentas con origen distinto"),
  ];
}

export const TAB_LABEL: Record<PlatformTab, string> = { ...Object.fromEntries(PLATFORMS.map((p) => [p, PLATFORM_META[p].label])), none: "Sin plataforma" } as Record<PlatformTab, string>;

export function platformSheet(
  units: CuadreUnit[],
  accounts: RawAccount[],
  tab: PlatformTab,
  retrans?: RetransmissionsByCompany,
  only?: Set<number> // cuentas a exportar (la vista filtrada); sin esto, todas
): XlsxSheet {
  const rows = buildPlatformRows(units, accounts, tab, retrans)
    .filter((r) => !only || only.has(r.id))
    .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name, "es"));
  const conds = conditionKeys(rows);
  return {
    name: TAB_LABEL[tab],
    headers: [
      "cuenta", "razon social", "facturacion de cuenta", "etiqueta de cuenta", "retransmision", "unidades", "activas", "facturables",
      "no facturables", "inactivas", "reportan", "sin reportar", ...conds.map((c) => c.toLowerCase()),
    ],
    rows: rows.map((r) => [
      r.name || "(sin cuenta)", r.company, r.billableAccount ? "Facturable" : "No facturable", r.accountCondition ?? "",
      r.retransmission == null ? "sin dato" : r.retransmission.join(", "),
      r.total, r.active, r.billable, r.notBillable, r.inactive, r.reporting, r.silent, ...conds.map((c) => r.conditions[c] ?? 0),
    ]),
  };
}

export const platformsSheets = (units: CuadreUnit[], accounts: RawAccount[], retrans?: RetransmissionsByCompany) =>
  ([...PLATFORMS, "none"] as PlatformTab[]).map((tab) => platformSheet(units, accounts, tab, retrans));

// Facturación: datos fiscales de la cuenta y cuántas unidades facturables tiene
// en cada plataforma (lo que se cobra contra lo que cada proveedor nos cobra).
export function facturacionSheet(units: CuadreUnit[], accounts: RawAccount[]): XlsxSheet {
  const counts = new Map<number, Record<string, number>>();
  for (const u of units) {
    if (u.accountId == null) continue;
    const c = counts.get(u.accountId) ?? {};
    c[u.billing] = (c[u.billing] ?? 0) + 1;
    if (u.billing === "billable") {
      if (!u.platforms.length) c.none = (c.none ?? 0) + 1;
      for (const p of u.platforms) c[p] = (c[p] ?? 0) + 1;
    }
    counts.set(u.accountId, c);
  }
  const rows: XlsxCell[][] = [...accounts]
    .sort((a, b) => (counts.get(b.id)?.billable ?? 0) - (counts.get(a.id)?.billable ?? 0) || a.name.localeCompare(b.name, "es"))
    .map((a) => {
      const c = counts.get(a.id) ?? {};
      const b = a.billingInfo;
      return [
        a.name, a.company ?? "", b?.taxId ?? "", b?.email ?? "", b?.phone ?? "", b?.city ?? "", b?.state ?? "",
        a.billable ? "Facturable" : "No facturable", a.condition ?? "",
        c.billable ?? 0, ...PLATFORMS.map((p) => c[p] ?? 0), c.none ?? 0, c.notBillable ?? 0, c.inactive ?? 0,
      ];
    });
  return {
    name: "Facturación",
    headers: [
      "cuenta", "razon social", "rfc", "correo", "telefono", "ciudad", "estado", "facturacion de cuenta", "etiqueta de cuenta",
      "unidades facturables", ...PLATFORMS.map((p) => `facturables en ${PLATFORM_META[p].label.toLowerCase()}`),
      "facturables sin plataforma", "activas no facturables", "inactivas",
    ],
    rows,
  };
}

// ── Barridos homologados (formato Wialon), uno por plataforma o todos en zip ──

export async function homologadoBytes(platform: SgPlatform, units: CuadreUnit[], cutoff: Date) {
  const inPlatform = units.filter((u) => u.platforms.includes(platform));
  return new Uint8Array(await buildHomologado(platform, inPlatform, { cutoff }).xlsx.writeBuffer());
}

export async function downloadHomologado(platform: SgPlatform, units: CuadreUnit[], cutoff: Date) {
  downloadBytes(homologadoFileName(platform, cutoff), await homologadoBytes(platform, units, cutoff), "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
}

export async function downloadHomologadoZip(units: CuadreUnit[], cutoff: Date) {
  const entries = [];
  for (const p of PLATFORMS) entries.push({ name: homologadoFileName(p, cutoff), data: await homologadoBytes(p, units, cutoff) });
  downloadBytes(`barridos ${barridoMonth(cutoff)}.zip`, buildZip(entries), "application/zip");
}

// ── Informes que leen Supabase (antes vivían en Barrido y Usuarios) ──────────

// Sin sg-realtime el informe sale igual, sin la columna de plataformas (como antes).
const originsFailed = (e: Error) => {
  notifyError(e.message);
  return {};
};

export async function exportBarrido(onProgress: (stage: "devices" | "positions", n: number) => void): Promise<number> {
  const all: any[] = [];
  for (let from = 0; ; from += 1000) {
    const { data, error } = await sgSupabase.from("devices").select(BARRIDO_SELECT).order("id", { ascending: true }).range(from, from + 999);
    if (error) throw new Error(error.message);
    all.push(...(data ?? []));
    onProgress("devices", all.length);
    if (!data || data.length < 1000) break;
  }
  all.sort((a, b) => (a.users?.user_name || "").localeCompare(b.users?.user_name || "", "es") || a.id - b.id);
  onProgress("positions", all.length);
  const imeis = all.map((d) => d.imei).filter(Boolean);
  const [posMap, originMap] = await Promise.all([fetchLastPositions(imeis, { requestId: "tabla" }), fetchOrigins(imeis).catch(originsFailed)]);
  await downloadXlsx(`barrido_${fileDate()}.xlsx`, "Barrido", BARRIDO_HEADERS, all.map((d) => barridoRow(d, posMap.get(d.imei), originMap)));
  return all.length;
}

export async function exportLineas(onProgress: (n: number) => void): Promise<number> {
  const all = await fetchAllLineas("", onProgress);
  await downloadXlsx(`barrido_cuadre_lineas_${fileDate()}.xlsx`, "Cuadre de lineas", LINEAS_HEADERS, all.map(lineaRow));
  return all.length;
}

// Sin nombre/apellido: en SG ahí venía la plataforma escrita a mano.
export const usuariosHeaders = (t: (k: string) => string) => [
  t("users.cols.user"),
  t("barrido.cols.platforms"),
  t("users.cols.inactive"),
  t("users.cols.active"),
  t("users.cols.billable"),
  t("users.cols.total"),
  t("users.cols.businessName"),
  t("users.cols.status"),
];

export async function exportUsuarios(headers: string[], search = ""): Promise<number> {
  const [all, imeisByUser] = await Promise.all([fetchAllSgUsers(search), fetchImeisByUser()]);
  const originMap = await fetchOrigins(Object.values(imeisByUser).flat()).catch(originsFailed);
  await downloadXlsx(`usuarios_${fileDate()}.xlsx`, "Usuarios", headers, all.map((u) => userRow(u, imeisByUser[u.id] ?? [], originMap)));
  return all.length;
}
